import type { IWorkingSite } from '../interfaces/IWorkingSite.js';
import type { UnifiedProject } from '../interfaces/IUnifiedProject.js';
import { handle } from './errors.js';
import type { AuthedFetch } from './transport.js';

/**
 * The projects API over an injected transport — each app binds its own
 * `authedFetch` in its `apis/projects.ts` and re-exports the result, so call
 * sites are identical in both apps and the contract lives once.
 */
export function createProjectsApi(authedFetch: AuthedFetch) {
  async function listProjects(signal?: AbortSignal): Promise<UnifiedProject[]> {
    const res = await authedFetch('/api/projects', { signal });
    if (!res.ok) return handle(res);
    const body = (await res.json()) as { projects: UnifiedProject[] };
    return body.projects;
  }

  async function openProject(joinKey: string): Promise<IWorkingSite> {
    const res = await authedFetch(`/api/projects/${encodeURIComponent(joinKey)}/open`, {
      method: 'POST',
      // Fastify rejects an empty body when Content-Type is application/json
      // (which authedFetch always sets). Send an empty object to satisfy the parser.
      body: '{}',
    });
    if (!res.ok) return handle(res);
    return (await res.json()) as IWorkingSite;
  }

  return { listProjects, openProject };
}
